import React, { useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowRight,
  Check,
  ChevronDown,
  Link2,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { COUNTRIES_LIST } from '../../constants';
import { db, type FreeTrafficWebsiteCheckResponse } from '../../services/db';
import { trackEvent } from '../../services/analytics';

const TRAFFIC_LOCATION_OPTIONS = [
  { code: 'ANY', name: 'Any location' },
  { code: 'WORLD', name: 'Worldwide mix' },
  ...COUNTRIES_LIST
    .filter(({ code }) => code !== 'AQ')
    .sort((a, b) => a.name.localeCompare(b.name)),
];

const FREE_TRAFFIC_DASHBOARD_HANDOFF_KEY = 'tc_free_traffic_dashboard_handoff';

type WebsiteCheckStatus = 'idle' | 'checking' | 'valid' | 'invalid' | 'error';

const initialForm = {
  email: '',
  whatsappNumber: '',
  websiteUrl: '',
  trafficLocation: 'Any location',
  honeypot: '',
};

const FreeTrafficClaimCard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const optionsId = useId();
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState(initialForm);
  const [countrySearch, setCountrySearch] = useState(initialForm.trafficLocation);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [websiteCheckStatus, setWebsiteCheckStatus] = useState<WebsiteCheckStatus>('idle');
  const [websiteCheck, setWebsiteCheck] = useState<FreeTrafficWebsiteCheckResponse | null>(null);
  const [websiteCheckMessage, setWebsiteCheckMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verificationSubmitting, setVerificationSubmitting] = useState(false);
  const [verificationLeadId, setVerificationLeadId] = useState('');
  const [verificationPhone, setVerificationPhone] = useState('');
  // Der Code geht nicht zwangslaeufig ueber WhatsApp: schlaegt der Versand fehl
  // (gesperrtes Konto, Nummer ohne WhatsApp), faellt das Backend auf E-Mail
  // zurueck und meldet den tatsaechlichen Kanal. Ohne dieses Feld stand hier
  // "WhatsApp verification for +49 ••• 567", waehrend der Code im Postfach lag.
  const [verificationChannel, setVerificationChannel] = useState('whatsapp');
  const [verificationCode, setVerificationCode] = useState('');
  const [debugVerificationCode, setDebugVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!countryDropdownRef.current?.contains(event.target as Node)) {
        setCountryDropdownOpen(false);
        setCountrySearch(form.trafficLocation);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [form.trafficLocation]);

  useEffect(() => {
    const websiteUrl = form.websiteUrl.trim();
    if (!websiteUrl || websiteUrl.length < 4 || !websiteUrl.includes('.')) {
      setWebsiteCheckStatus('idle');
      setWebsiteCheck(null);
      setWebsiteCheckMessage('');
      return;
    }

    let cancelled = false;
    setWebsiteCheckStatus('checking');
    setWebsiteCheck(null);
    setWebsiteCheckMessage('Checking domain, previous claims, and GA/GTM tags…');

    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await db.checkFreeTrafficWebsite(websiteUrl);
        if (cancelled) return;
        setWebsiteCheck(result);
        setWebsiteCheckStatus(result.eligible ? 'valid' : 'invalid');
        setWebsiteCheckMessage(result.eligible
          ? 'Your site passed the eligibility check.'
          : 'Fix the failed checks before requesting the test.');
      } catch (requestError) {
        if (cancelled) return;
        setWebsiteCheckStatus('error');
        setWebsiteCheck(null);
        setWebsiteCheckMessage(requestError instanceof Error
          ? requestError.message
          : 'Failed to check website eligibility');
      }
    }, 650);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [form.websiteUrl]);

  const resetVerification = () => {
    setVerificationLeadId('');
    setVerificationPhone('');
    setVerificationCode('');
    setDebugVerificationCode('');
  };

  const updateForm = (field: keyof typeof initialForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setError('');
    setSuccess('');
    if (field !== 'honeypot' && verificationLeadId) resetVerification();
  };

  const verifyClaim = async () => {
    const code = verificationCode.trim();
    if (!verificationLeadId) return;
    if (code.length < 4) {
      setError('Enter the verification code.');
      return;
    }

    setError('');
    setSuccess('');
    setVerificationSubmitting(true);
    try {
      const result = await db.verifyFreeTrafficClaim(verificationLeadId, code);
      sessionStorage.setItem(FREE_TRAFFIC_DASHBOARD_HANDOFF_KEY, JSON.stringify({
        email: result.email,
        temporaryPassword: result.temporary_password || '',
        projectId: result.project_id,
        message: result.message || 'Verified. Your account and campaign are now active.',
      }));
      trackEvent('free_traffic_claim_verified', {
        page: location.pathname,
        project_id: result.project_id,
        status: result.status,
      });
      navigate('/dashboard');
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Failed to verify the code';
      setError(message);
      trackEvent('free_traffic_claim_verify_error', { page: location.pathname, message });
    } finally {
      setVerificationSubmitting(false);
    }
  };

  const submitClaim = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (verificationLeadId) {
      await verifyClaim();
      return;
    }
    if (websiteCheckStatus === 'checking') {
      setError('Wait for the website eligibility check to finish.');
      return;
    }
    if (websiteCheckStatus === 'invalid' || (websiteCheck && !websiteCheck.eligible)) {
      setError('Fix the failed website checks before requesting free traffic.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await db.submitFreeTrafficClaim({
        email: form.email,
        whatsapp_number: form.whatsappNumber,
        website_url: form.websiteUrl,
        traffic_location: form.trafficLocation,
        page_url: window.location.href,
        honeypot: form.honeypot,
      });
      setSuccess(result.message || 'Your free traffic request was received.');
      if (result.verification_required) {
        setVerificationLeadId(result.lead_id || result.id);
        const channel = result.verification_channel || 'whatsapp';
        setVerificationChannel(channel);
        setVerificationPhone(
          channel === 'email'
            ? (result.email || form.email || 'your email address')
            : (result.masked_whatsapp || 'your WhatsApp number'),
        );
        setDebugVerificationCode(result.debug_verification_code || '');
      } else {
        resetVerification();
        setForm(initialForm);
        setCountrySearch(initialForm.trafficLocation);
      }
      trackEvent('free_traffic_claim_submit', {
        page: location.pathname,
        domain: result.normalized_domain,
        status: result.status,
      });
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Failed to submit free traffic request';
      setError(message);
      trackEvent('free_traffic_claim_error', { page: location.pathname, message });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredLocations = TRAFFIC_LOCATION_OPTIONS.filter(({ name }) => (
    name.toLowerCase().includes(countrySearch.trim().toLowerCase())
  )).slice(0, 12);
  const waitingForVerification = Boolean(verificationLeadId);
  const disabled = waitingForVerification
    ? verificationSubmitting || verificationCode.trim().length < 4
    : submitting || websiteCheckStatus === 'checking' || websiteCheckStatus === 'invalid';

  return (
    <form id="tcr-free-test-form" className="tcr-free-claim" onSubmit={submitClaim} aria-labelledby="tcr-free-claim-title">
      <input
        className="tcr-honeypot"
        type="text"
        name="company"
        value={form.honeypot}
        onChange={(event) => updateForm('honeypot', event.target.value)}
        tabIndex={-1}
        autoComplete="off"
      />
      <div className="tcr-free-claim__intro">
        <span><ShieldCheck size={16} /> Free GA4 test run</span>
        <h3 id="tcr-free-claim-title">Get 6,000 visitors after a quick eligibility check.</h3>
        <p>We check the domain, confirm your number, create the account, and start one eligible 24-hour test campaign.</p>
      </div>

      <div className="tcr-free-claim__grid">
        <label>
          <span><Mail size={14} /> Email</span>
          <input name="email" type="email" required disabled={waitingForVerification || submitting} value={form.email} onChange={(event) => updateForm('email', event.target.value)} placeholder="you@company.com" autoComplete="email" />
        </label>
        <label>
          {/* Freiwillig: ohne Nummer verschickt das Backend den Code per E-Mail.
              Die Pflicht schloss vorher jeden aus, der kein WhatsApp hat — in den
              USA ist eine Nummer ohne WhatsApp der Normalfall. */}
          <span><Phone size={14} /> Phone number <small>(optional)</small></span>
          <input name="whatsapp" type="tel" disabled={waitingForVerification || submitting} value={form.whatsappNumber} onChange={(event) => updateForm('whatsappNumber', event.target.value)} placeholder="Leave empty to verify by email" autoComplete="tel" />
        </label>
        <label>
          <span><Link2 size={14} /> Website domain</span>
          <input name="website" type="text" required disabled={waitingForVerification || submitting} value={form.websiteUrl} onChange={(event) => updateForm('websiteUrl', event.target.value)} placeholder="example.com" autoComplete="url" />
        </label>
        <div className="tcr-free-claim__location" ref={countryDropdownRef}>
          <span className="tcr-free-claim__label"><MapPin size={14} /> Visitor location</span>
          <div className="tcr-free-claim__combobox">
            <Search size={15} />
            <input
              type="text"
              required
              role="combobox"
              aria-expanded={countryDropdownOpen}
              aria-controls={optionsId}
              disabled={waitingForVerification || submitting}
              value={countrySearch}
              onFocus={(event) => { setCountryDropdownOpen(true); event.currentTarget.select(); }}
              onChange={(event) => {
                setCountrySearch(event.target.value);
                updateForm('trafficLocation', event.target.value);
                setCountryDropdownOpen(true);
              }}
              placeholder="Search country"
            />
            <ChevronDown size={15} />
          </div>
          {countryDropdownOpen && (
            <div className="tcr-free-claim__options" id={optionsId} role="listbox">
              {filteredLocations.length ? filteredLocations.map(({ code, name }) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={form.trafficLocation === name}
                  key={code}
                  onClick={() => {
                    updateForm('trafficLocation', name);
                    setCountrySearch(name);
                    setCountryDropdownOpen(false);
                  }}
                >
                  <span>{name}</span><small>{code}</small>
                </button>
              )) : <p>No country found</p>}
            </div>
          )}
        </div>
      </div>

      {websiteCheckStatus !== 'idle' && (
        <div className={`tcr-free-claim__check is-${websiteCheckStatus}`} role="status">
          {websiteCheckStatus === 'checking'
            ? <Loader2 size={16} className="tcr-spin" />
            : websiteCheckStatus === 'valid'
              ? <Check size={16} />
              : <AlertCircle size={16} />}
          <div>
            <strong>{websiteCheckMessage}</strong>
            {websiteCheck?.checks.map((check) => (
              <small key={check.key}>{check.passed ? '✓' : '×'} {check.label}: {check.message}</small>
            ))}
          </div>
        </div>
      )}

      {waitingForVerification && (
        <label className="tcr-free-claim__verification">
          <span>{verificationChannel === 'email' ? 'Email' : 'WhatsApp'} verification for {verificationPhone}</span>
          <input name="verificationCode" type="text" required inputMode="numeric" autoComplete="one-time-code" value={verificationCode} onChange={(event) => setVerificationCode(event.target.value.replace(/\s+/g, '').slice(0, 12))} placeholder="Verification code" />
          {debugVerificationCode && <small>Local test code: <strong>{debugVerificationCode}</strong></small>}
        </label>
      )}

      {error && <div className="tcr-free-claim__message is-error" role="alert"><AlertCircle size={16} /> {error}</div>}
      {success && <div className="tcr-free-claim__message is-success" role="status"><Check size={16} /> {success}</div>}

      <button className="tcr-button tcr-button--primary tcr-free-claim__submit" type="submit" disabled={disabled}>
        {verificationSubmitting
          ? <><Loader2 size={17} className="tcr-spin" /> Verifying</>
          : submitting
            ? <><Loader2 size={17} className="tcr-spin" /> Sending request</>
            : waitingForVerification
              ? <>Verify and start test <ArrowRight size={17} /></>
              : <>Start free 24-hour test <ArrowRight size={17} /></>}
      </button>
      <p className="tcr-free-claim__fineprint">No credit card · One eligible public domain · Starts after verification</p>
    </form>
  );
};

export default FreeTrafficClaimCard;
